import { useEffect, useId, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useFrappeAuth } from "frappe-react-sdk";

import { OwnerFlowBrand } from "../../../components/OwnerFlowLogo";
import { LanguageSelector } from "../../../components/LanguageSelector";
import { buttonVariants } from "../../../components/ui/button";
import { cn } from "../../../lib/cn";
import { navLabel } from "../../content/navLabels";
import {
  AUDIENCE_NAV_IDS,
  HEADER_NAV_IDS,
  pagePath,
  signupWithIntent,
  type MarketingPageId,
} from "../../content/routes";
import { useMarketingContext } from "../../hooks/MarketingContext";
import { useMarketingLang } from "../../hooks/useMarketingPage";

const SECONDARY_NAV: MarketingPageId[] = ["pricing", "help"];

export function MarketingHeader() {
  const { t } = useTranslation();
  const lang = useMarketingLang();
  const location = useLocation();
  const { founderEnabled } = useMarketingContext();
  const { currentUser } = useFrappeAuth();
  const isLoggedIn = Boolean(currentUser && currentUser !== "Guest");
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileMenuId = useId();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    const desktop = window.matchMedia("(min-width: 1024px)");
    const onBreakpoint = () => {
      if (desktop.matches) setMobileOpen(false);
    };
    desktop.addEventListener("change", onBreakpoint);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      desktop.removeEventListener("change", onBreakpoint);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const trialHref = founderEnabled ? "/access" : signupWithIntent("manager");
  const trialLabel = founderEnabled
    ? t("marketing.cta.founder", "Rejoindre le Programme Fondateur")
    : t("marketing.cta.trial", "Commencer l'essai gratuit");
  const loginHref = isLoggedIn ? "/dashboard" : "/login";
  const loginLabel = isLoggedIn
    ? t("help.backToApp", "Retour à l'application")
    : t("marketing.cta.login", "Connexion");

  const linkLabel = (id: MarketingPageId) =>
    founderEnabled && id === "pricing" ? t("marketing.nav.founder", "Programme Fondateur") : navLabel(id, t);

  const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-lg px-2 text-sm text-neutral-900 transition-colors hover:bg-neutral-50 xl:px-2.5",
      isActive ? "font-bold" : "font-medium",
    );

  const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex min-h-11 items-center rounded-xl px-3 text-base text-neutral-900 hover:bg-neutral-50",
      isActive ? "bg-neutral-50 font-bold" : "font-medium",
    );

  return (
    <>
      {/*
        `#root` porte `overflow-x: hidden` sous 768px, ce qui casse `position: sticky`
        (l'en-tête défile au lieu de se coller). D'où le `fixed` en mobile + le
        décalage plus bas, comme sur l'ancienne landing.
      */}
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 bg-white/90 pt-safe backdrop-blur-md md:sticky md:inset-x-auto md:pt-0",
          mobileOpen
            ? "bottom-0 flex flex-col overflow-hidden md:fixed md:inset-x-0"
            : "border-b border-border",
        )}
      >
        <div
          className={cn(
            "mx-auto flex h-16 w-full max-w-7xl shrink-0 items-center gap-2 px-4 sm:px-6",
            mobileOpen && "border-b border-border",
          )}
        >
          <Link
            to={pagePath("home", lang)}
            className="flex shrink-0 items-center gap-2 text-neutral-900 hover:text-neutral-900"
          >
            <OwnerFlowBrand hideWordmarkClassName="max-[360px]:hidden" />
          </Link>

          <div className="hidden min-w-0 flex-1 lg:block" aria-hidden />

          <nav
            className="hidden shrink-0 items-center gap-0.5 lg:flex"
            aria-label={t("marketing.nav.main", "Navigation principale")}
          >
            {HEADER_NAV_IDS.map((id) => (
              <NavLink key={id} to={pagePath(id, lang)} className={desktopLinkClass}>
                {linkLabel(id)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden min-w-0 flex-1 lg:block" aria-hidden />

          <div className="ml-auto flex min-w-0 items-center gap-2 lg:ml-0">
            <Link
              to={loginHref}
              className={cn(
                buttonVariants({ variant: isLoggedIn ? "default" : "outline" }),
                "inline-flex h-9 min-h-9 shrink-0 whitespace-nowrap rounded-lg px-3 text-sm font-medium sm:px-4",
              )}
            >
              {loginLabel}
            </Link>
            <Link
              to={trialHref}
              className={cn(buttonVariants(), "hidden h-9 min-h-9 shrink-0 whitespace-nowrap rounded-lg px-4 text-sm font-medium lg:inline-flex")}
            >
              {trialLabel}
            </Link>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-neutral-900 hover:bg-[#FAFAF8] lg:hidden"
              aria-expanded={mobileOpen}
              aria-controls={mobileMenuId}
              aria-label={
                mobileOpen
                  ? t("marketing.nav.close", "Fermer le menu")
                  : t("marketing.nav.open", "Ouvrir le menu")
              }
              onClick={() => setMobileOpen((open) => !open)}
            >
              {mobileOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
            </button>
            <LanguageSelector className="h-9 w-9 min-h-9 shrink-0 rounded-lg" />
          </div>
        </div>

        {mobileOpen ? (
          <div
            id={mobileMenuId}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-5 pb-[calc(1.25rem+var(--safe-bottom))] lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label={t("marketing.nav.main", "Navigation principale")}
          >
            <div className="space-y-1">
              {AUDIENCE_NAV_IDS.map((id) => (
                <NavLink key={id} to={pagePath(id, lang)} className={mobileLinkClass}>
                  {navLabel(id, t)}
                </NavLink>
              ))}
            </div>
            <div className="mt-5 space-y-1 border-t border-neutral-100 pt-5">
              {SECONDARY_NAV.map((id) => (
                <NavLink key={id} to={pagePath(id, lang)} className={mobileLinkClass}>
                  {linkLabel(id)}
                </NavLink>
              ))}
              <Link
                to={loginHref}
                className="flex min-h-11 items-center rounded-xl px-3 text-base font-medium text-neutral-900 hover:bg-neutral-50"
              >
                {loginLabel}
              </Link>
            </div>
            <div className="mt-auto border-t border-neutral-100 pt-5">
              <Link to={trialHref} className={cn(buttonVariants(), "h-11 w-full rounded-lg text-[15px] font-medium")}>
                {trialLabel}
              </Link>
            </div>
          </div>
        ) : null}
      </header>

      <div className="h-[calc(4rem+var(--safe-top))] md:hidden" aria-hidden />
    </>
  );
}
